import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Camera, CheckCircle2, ExternalLink, Loader2, Move, Pencil, Trash2 } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { DEFAULT_PHOTO_FOCAL_POINT, type PhotoFocalPoint } from '../lib/photoFocalPoint';
import type { ApiStoreRecord } from '../lib/mergeStoreProfile';
import type { StorefrontPhotoSource, StorefrontPhotoVerificationStatus } from '../types';
import { StorefrontPhotoPositionEditor } from './StorefrontPhotoPositionEditor';

interface StorefrontPhotoUploadProps {
  storeId: number;
  storeIdParam: string;
  hasPhoto: boolean;
  imageUrl?: string | null;
  photoSource?: StorefrontPhotoSource;
  verificationStatus?: StorefrontPhotoVerificationStatus;
  sourcePageUrl?: string | null;
  focalPoint?: PhotoFocalPoint;
  className?: string;
  variant?: 'inline' | 'menu';
}

async function invalidateStoreProfileQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storeIdParam: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'storefront-photo'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/stores', 'store-profiles'] }),
  ]);
}

export function StorefrontPhotoUpload({
  storeId,
  storeIdParam,
  hasPhoto,
  imageUrl,
  photoSource,
  verificationStatus,
  sourcePageUrl,
  focalPoint = DEFAULT_PHOTO_FOCAL_POINT,
  className,
  variant = 'inline',
}: StorefrontPhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [positionEditorOpen, setPositionEditorOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose a JPG or PNG image.', variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    setMenuOpen(false);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiRequest(`/api/store-profiles/${storeId}/storefront-photo`, 'POST', formData);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.message ?? body.error ?? 'Upload failed');
      }

      const nextImageUrl = body.imageUrl ?? body.photoUrl ?? null;
      queryClient.setQueryData(
        ['/api/stores', storeIdParam, 'detail', 'live-photo-v2'],
        (current: ApiStoreRecord | undefined) => {
          if (!current) return current;
          return {
            ...current,
            photoUrl: nextImageUrl,
            hasStorePhoto: Boolean(nextImageUrl),
            storefrontPhotoSource: body.storefrontPhotoSource ?? 'uploaded',
            storefrontPhotoVerificationStatus: body.storefrontPhotoVerificationStatus ?? 'confirmed',
            photoFocalX: body.photoFocalX ?? 50,
            photoFocalY: body.photoFocalY ?? 50,
          };
        },
      );
      queryClient.setQueryData(
        ['/api/store-profiles', storeIdParam, 'storefront-photo'],
        body,
      );
      await invalidateStoreProfileQueries(queryClient, storeIdParam);
      toast({
        title: photoSource === 'google_street_view_demo' ? 'Confirmed photo uploaded' : 'Storefront photo replaced',
        description: 'Adjust framing if the banner crops awkwardly.',
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Could not upload storefront photo',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    setIsUploading(true);
    setMenuOpen(false);
    try {
      const response = await apiRequest(`/api/store-profiles/${storeId}/storefront-photo`, 'DELETE');
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to remove storefront photo');
      }

      await invalidateStoreProfileQueries(queryClient, storeIdParam);
      toast({ title: 'Storefront photo removed' });
    } catch (error) {
      toast({
        title: 'Remove failed',
        description: error instanceof Error ? error.message : 'Could not remove photo',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openFramingEditor = () => {
    setMenuOpen(false);
    setPositionEditorOpen(true);
  };

  const handleConfirmCandidate = async () => {
    setIsUploading(true);
    setMenuOpen(false);
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/storefront-photo/verification`,
        'PATCH',
        { status: 'confirmed' },
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to confirm storefront image');
      }
      await invalidateStoreProfileQueries(queryClient, storeIdParam);
      toast({ title: 'Storefront image confirmed' });
    } catch (error) {
      toast({
        title: 'Confirmation failed',
        description: error instanceof Error ? error.message : 'Could not confirm storefront image',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const actionButtons = (
    <>
      {photoSource === 'google_image_search' && verificationStatus === 'to_be_confirmed' && (
        <Button
          type="button"
          size="sm"
          variant={variant === 'menu' ? 'ghost' : 'secondary'}
          className={variant === 'menu' ? 'w-full justify-start text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50' : ''}
          disabled={isUploading}
          onClick={() => void handleConfirmCandidate()}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Confirm image
        </Button>
      )}
      {sourcePageUrl && photoSource === 'google_image_search' && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={variant === 'menu' ? 'w-full justify-start' : ''}
          asChild
        >
          <a href={sourcePageUrl} target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            View source
          </a>
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        variant={variant === 'menu' ? 'ghost' : 'secondary'}
        className={variant === 'menu' ? 'w-full justify-start' : 'bg-white/90 hover:bg-white text-gray-900 shadow'}
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Camera className="h-4 w-4 mr-2" />
        )}
        {photoSource === 'google_street_view_demo'
          ? 'Upload confirmed photo'
          : hasPhoto
            ? 'Replace photo'
            : 'Add photo'}
      </Button>
      {hasPhoto && imageUrl && photoSource !== 'google_street_view_demo' && (
        <Button
          type="button"
          size="sm"
          variant={variant === 'menu' ? 'ghost' : 'secondary'}
          className={variant === 'menu' ? 'w-full justify-start' : 'bg-white/90 hover:bg-white text-gray-900 shadow'}
          disabled={isUploading}
          onClick={openFramingEditor}
        >
          <Move className="h-4 w-4 mr-2" />
          Adjust framing
        </Button>
      )}
      {(photoSource === 'uploaded' || photoSource === 'google_image_search') && (
        <Button
          type="button"
          size="sm"
          variant={variant === 'menu' ? 'ghost' : 'outline'}
          className={
            variant === 'menu'
              ? 'w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50'
              : 'bg-white/80 hover:bg-white border-red-200 text-red-700'
          }
          disabled={isUploading}
          onClick={() => void handleRemove()}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Remove image
        </Button>
      )}
    </>
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {variant === 'menu' ? (
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={`h-8 w-8 rounded-full bg-black/45 text-white hover:bg-black/60 hover:text-white border border-white/20 backdrop-blur-sm ${className ?? ''}`}
              disabled={isUploading}
              aria-label="Edit storefront photo"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-3.5 w-3.5" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-52 p-2 space-y-1">
            <p className="px-2 py-1 text-xs font-medium text-gray-500">Storefront photo</p>
            {actionButtons}
          </PopoverContent>
        </Popover>
      ) : (
        <div className={`flex flex-wrap gap-2 ${className ?? ''}`}>
          {actionButtons}
        </div>
      )}

      {hasPhoto && imageUrl && photoSource !== 'google_street_view_demo' && (
        <StorefrontPhotoPositionEditor
          storeId={storeId}
          storeIdParam={storeIdParam}
          imageUrl={imageUrl}
          focalPoint={focalPoint}
          open={positionEditorOpen}
          onOpenChange={setPositionEditorOpen}
        />
      )}
    </>
  );
}
